import { Alert, Statistic, Table } from "antd";
import { FinanceEntryType } from "../typeDefs";
import { isInGstRegisteredPeriod } from "./taxConfig";

const money = (value: number) => `$${value.toFixed(2)}`;
const gstPortion = (amount: number) => (amount * 3) / 23;

export const FinanceOverview = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const entries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]);

  const revenue = entries
    .filter((item) => item.category === "Revenue")
    .reduce((total, item) => total + item.amount, 0);

  const expenses = entries
    .filter((item) => item.category === "Expenses")
    .reduce((total, item) => total + item.amount, 0);

  const assets = entries
    .filter((item) => item.category === "Assets")
    .reduce((total, item) => total + item.amount, 0);

  const gstCollected = entries
    .filter((item) => item.category === "Revenue" && isInGstRegisteredPeriod(item.date) && item.gstIncluded && item.gstRegistered)
    .reduce((total, item) => total + gstPortion(item.amount), 0);

  const gstClaimable = entries
    .filter(
      (item) =>
        (item.category === "Expenses" || item.category === "Assets") &&
        isInGstRegisteredPeriod(item.date) &&
        item.gstIncluded &&
        item.gstRegistered,
    )
    .reduce((total, item) => total + gstPortion(item.amount) * ((item.apportionRate ?? 100) / 100), 0);

  const missingReceipts = entries.filter(
    (item) =>
      (item.category === "Expenses" || item.category === "Assets") &&
      !item.receipt,
  );

  const unreconciled = entries.filter((item) => !item.bankTransactionId && !item.receipt);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
        <Statistic title="Revenue" value={revenue} precision={2} prefix="$" />
        <Statistic title="Expenses" value={expenses} precision={2} prefix="$" />
        <Statistic title="Asset purchases" value={assets} precision={2} prefix="$" />
        <Statistic title="Profit estimate" value={revenue - expenses} precision={2} prefix="$" />
        <Statistic
          title={gstCollected - gstClaimable >= 0 ? "GST payable" : "GST refundable"}
          value={Math.abs(gstCollected - gstClaimable)}
          precision={2}
          prefix="$"
        />
      </div>

      {(missingReceipts.length > 0 || unreconciled.length > 0) && (
        <Alert
          type="warning"
          showIcon
          message="Finance review needed"
          description={`${missingReceipts.length} entries are missing receipts. ${unreconciled.length} entries are not linked to a bank import or receipt.`}
        />
      )}

      <Table
        title={() => "Missing receipts"}
        size="small"
        pagination={{ pageSize: 5 }}
        dataSource={missingReceipts.map((item) => ({ ...item, key: item.id }))}
        columns={[
          { title: "Date", dataIndex: "date", render: (value) => new Date(value).toLocaleDateString("en-GB") },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Category", dataIndex: "category" },
          { title: "Description", dataIndex: "description" },
          { title: "Amount", dataIndex: "amount", render: money },
        ]}
      />
    </div>
  );
};
